import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/user/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/user/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) {
      const d = await res.json();
      throw new Error(d.detail || 'Login failed');
    }
    const data = await res.json();
    setUser(data);
    return data;
  };

  const register = async (name, email, phone, password) => {
    const res = await fetch(`${API}/api/user/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, email, phone, password })
    });
    if (!res.ok) {
      const d = await res.json();
      const detail = d.detail;
      if (Array.isArray(detail)) throw new Error(detail.map(e => e.msg || JSON.stringify(e)).join('. '));
      throw new Error(detail || 'Registration failed');
    }
    const data = await res.json();
    setUser(data);
    return data;
  };

  const logout = async () => {
    await fetch(`${API}/api/user/logout`, { method: 'POST', credentials: 'include' });
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const res = await fetch(`${API}/api/user/me`, { credentials: 'include' });
      if (res.ok) setUser(await res.json());
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
